import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, Truck, Calendar, MapPin, Navigation, Plus,
  DollarSign, FileText, Settings, Calculator, Route
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { partiesAPI, expensesAPI } from '../../services/part2-api';

const VEHICLE_TYPES = [
  { value: 'car', label: 'Car', rate: 8.00, icon: '🚗' },
  { value: 'motorcycle', label: 'Motorcycle', rate: 4.00, icon: '🏍️' },
  { value: 'auto', label: 'Auto Rickshaw', rate: 5.00, icon: '🛺' },
  { value: 'bicycle', label: 'Bicycle', rate: 2.00, icon: '🚲' },
  { value: 'custom', label: 'Custom Rate', rate: 0, icon: '⚙️' },
];

const MileageExpensePage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;

  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    vehicle_type: 'car',
    distance: 0,
    rate_per_km: 8.00,
    from_location: '',
    to_location: '',
    purpose: '',
    customer_id: '',
    customer_name: '',
    billable: false,
    odometer_start: '',
    odometer_end: '',
    notes: '',
  });

  const [trips, setTrips] = useState([]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await partiesAPI.list({ type: 'customer' });
      const data = response.data;
      setCustomers(data.parties || data || []);
    } catch (e) { console.error(e); }
  };

  const handleVehicleChange = (vehicleType) => {
    const vehicle = VEHICLE_TYPES.find(v => v.value === vehicleType);
    setFormData({
      ...formData,
      vehicle_type: vehicleType,
      rate_per_km: vehicle?.rate || 0,
    });
  };

  const calculateDistance = () => {
    if (formData.odometer_start && formData.odometer_end) {
      const distance = parseFloat(formData.odometer_end) - parseFloat(formData.odometer_start);
      if (distance > 0) {
        setFormData({ ...formData, distance });
      }
    }
  };

  const totalAmount = (formData.distance || 0) * (formData.rate_per_km || 0);

  const addTrip = () => {
    if (!formData.from_location || !formData.to_location || !formData.distance) {
      toast.error('Please fill in trip details');
      return;
    }
    setTrips([...trips, { ...formData, id: Date.now(), amount: totalAmount }]);
    setFormData({
      ...formData,
      from_location: '',
      to_location: '',
      distance: 0,
      odometer_start: '',
      odometer_end: '',
      purpose: '',
    });
  };

  const removeTrip = (id) => {
    setTrips(trips.filter(t => t.id !== id));
  };

  const handleSubmit = async () => {
    const allTrips = trips.length > 0 ? trips : [{ ...formData, amount: totalAmount }];
    
    if (allTrips.length === 0 || !allTrips[0].distance) {
      toast.error('Please add at least one trip');
      return;
    }

    setSaving(true);
    try {
      for (const trip of allTrips) {
        const payload = {
          date: trip.date,
          category: 'Transportation',
          amount: trip.amount,
          payment_mode: 'CASH',
          notes: `Mileage: ${trip.from_location} → ${trip.to_location} (${trip.distance} km @ ₹${trip.rate_per_km}/km). ${trip.purpose || ''}`.trim(),
          reference_no: `MILEAGE-${Date.now()}`,
          billable: trip.billable,
          customer_id: trip.customer_id,
        };

        await expensesAPI.create(payload);
      }

      toast.success(`${allTrips.length} mileage expense(s) recorded`);
      navigate(`/business/${bid}/expenses`);
    } catch (error) {
      toast.error('Error saving mileage expense');
    }
    setSaving(false);
  };

  const selectCustomer = (customer) => {
    setFormData({ ...formData, customer_id: customer.id, customer_name: customer.name });
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  const grandTotal = trips.reduce((sum, t) => sum + t.amount, 0) + (trips.length === 0 ? totalAmount : 0);

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-6 py-4 shadow-sm">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-slate-800">Record Mileage</h1>
              <p className="text-sm text-slate-500">Track travel expenses by distance</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="px-4 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium">
              Cancel
            </button>
            <button 
              onClick={handleSubmit} 
              disabled={saving} 
              className="px-5 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Main Form */}
          <div className="flex-1 space-y-5">
            
            {/* Vehicle Selection */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
              <h2 className="font-semibold text-slate-800 mb-5 flex items-center gap-2">
                <Truck className="w-5 h-5 text-red-600" />
                Vehicle & Rate
              </h2>
              
              <div className="grid grid-cols-5 gap-3 mb-5">
                {VEHICLE_TYPES.map(vehicle => (
                  <button
                    key={vehicle.value}
                    onClick={() => handleVehicleChange(vehicle.value)}
                    className={`p-4 rounded-xl border-2 transition-all text-center ${
                      formData.vehicle_type === vehicle.value 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <span className="text-2xl mb-2 block">{vehicle.icon}</span>
                    <p className={`text-xs font-medium ${formData.vehicle_type === vehicle.value ? 'text-red-700' : 'text-slate-600'}`}>
                      {vehicle.label}
                    </p>
                    {vehicle.value !== 'custom' && (
                      <p className="text-[10px] text-slate-400 mt-1">₹{vehicle.rate}/km</p>
                    )}
                  </button>
                ))}
              </div>

              {formData.vehicle_type === 'custom' && (
                <div className="flex items-center gap-3">
                  <label className="text-sm text-slate-600 font-medium">Custom Rate (₹/km):</label>
                  <input
                    type="number"
                    value={formData.rate_per_km}
                    onChange={(e) => setFormData({ ...formData, rate_per_km: parseFloat(e.target.value) || 0 })}
                    className="w-32 px-3 py-2 border border-slate-200 rounded-lg text-sm text-right"
                    step="0.50"
                    min="0"
                  />
                </div>
              )}
            </div>

            {/* Trip Details */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
              <h2 className="font-semibold text-slate-800 mb-5 flex items-center gap-2">
                <Route className="w-5 h-5 text-red-600" />
                Trip Details
              </h2>
              
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-slate-600 mb-1.5 font-medium">Date *</label>
                  <input 
                    type="date" 
                    value={formData.date} 
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })} 
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm" 
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1.5 font-medium">Purpose</label>
                  <input 
                    type="text" 
                    value={formData.purpose} 
                    onChange={(e) => setFormData({ ...formData, purpose: e.target.value })} 
                    placeholder="e.g., Client meeting, Site visit"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm" 
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1.5 font-medium">From Location *</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                      type="text" 
                      value={formData.from_location} 
                      onChange={(e) => setFormData({ ...formData, from_location: e.target.value })} 
                      placeholder="Starting point"
                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm" 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-1.5 font-medium">To Location *</label>
                  <div className="relative">
                    <Navigation className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input 
                      type="text" 
                      value={formData.to_location} 
                      onChange={(e) => setFormData({ ...formData, to_location: e.target.value })} 
                      placeholder="Destination"
                      className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-lg text-sm" 
                    />
                  </div>
                </div>
              </div>

              {/* Odometer or Manual Distance */}
              <div className="mt-5 p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-600 font-medium mb-3">Calculate Distance</p>
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <label className="block text-xs text-slate-500 mb-1">Odometer Start (km)</label>
                    <input 
                      type="number" 
                      value={formData.odometer_start} 
                      onChange={(e) => setFormData({ ...formData, odometer_start: e.target.value })} 
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" 
                      placeholder="0"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-slate-500 mb-1">Odometer End (km)</label>
                    <input 
                      type="number" 
                      value={formData.odometer_end} 
                      onChange={(e) => setFormData({ ...formData, odometer_end: e.target.value })} 
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" 
                      placeholder="0"
                    />
                  </div>
                  <button 
                    onClick={calculateDistance}
                    className="px-4 py-2 bg-slate-200 hover:bg-slate-300 rounded-lg text-sm font-medium text-slate-700 flex items-center gap-2"
                  >
                    <Calculator className="w-4 h-4" />
                    Calculate
                  </button>
                  <div className="text-center px-2">
                    <span className="text-slate-400">OR</span>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-slate-500 mb-1">Enter Distance (km)</label>
                    <input 
                      type="number" 
                      value={formData.distance || ''} 
                      onChange={(e) => setFormData({ ...formData, distance: parseFloat(e.target.value) || 0 })} 
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium text-right" 
                      placeholder="0"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>

              {/* Billable */}
              <div className="mt-5 flex items-center gap-3">
                <button
                  onClick={() => setFormData({ ...formData, billable: !formData.billable })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${formData.billable ? 'bg-red-600' : 'bg-slate-200'}`}
                >
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.billable ? 'left-7' : 'left-1'}`} />
                </button>
                <span className="text-sm text-slate-600">Billable to customer</span>
              </div>

              {formData.billable && (
                <div className="mt-4 relative">
                  <label className="block text-sm text-slate-600 mb-1.5 font-medium">Customer</label>
                  <input 
                    type="text"
                    value={customerSearch}
                    onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    placeholder="Search customer..."
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm"
                  />
                  {showCustomerDropdown && customerSearch && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto z-20">
                      {customers.filter(c => c.name?.toLowerCase().includes(customerSearch.toLowerCase())).map(c => (
                        <div key={c.id} onClick={() => selectCustomer(c)} className="px-3 py-2 hover:bg-red-50 cursor-pointer text-sm">
                          {c.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Add Trip Button */}
              <div className="mt-5 flex justify-end">
                <button
                  onClick={addTrip}
                  className="px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Another Trip
                </button>
              </div>
            </div>

            {/* Added Trips */}
            {trips.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                <h2 className="font-semibold text-slate-800 mb-4">Added Trips ({trips.length})</h2>
                <div className="space-y-3">
                  {trips.map((trip, idx) => (
                    <div key={trip.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-4">
                        <span className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold">{idx + 1}</span>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{trip.from_location} → {trip.to_location}</p>
                          <p className="text-xs text-slate-500">{trip.distance} km @ ₹{trip.rate_per_km}/km • {trip.purpose || 'No purpose'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="font-bold text-red-600">₹{trip.amount.toFixed(2)}</p>
                        <button onClick={() => removeTrip(trip.id)} className="text-slate-400 hover:text-red-500">
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Summary */}
          <div className="w-80 flex-shrink-0">
            <div className="sticky top-24">
              <div className="bg-gradient-to-br from-red-600 to-rose-700 rounded-xl shadow-xl p-5 text-white">
                <p className="text-red-200 text-xs uppercase tracking-wider font-semibold mb-1">Current Trip</p>
                <p className="text-3xl font-bold mb-4">₹{totalAmount.toFixed(2)}</p>
                
                <div className="space-y-2 text-sm border-t border-red-400/30 pt-4">
                  <div className="flex justify-between">
                    <span className="text-red-200">Distance</span>
                    <span className="font-medium">{formData.distance || 0} km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-200">Rate</span>
                    <span className="font-medium">₹{formData.rate_per_km}/km</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-red-200">Vehicle</span>
                    <span className="font-medium">{VEHICLE_TYPES.find(v => v.value === formData.vehicle_type)?.label || 'Car'}</span>
                  </div>
                </div>

                {trips.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-red-400/50">
                    <div className="flex justify-between items-center">
                      <span className="text-red-200">Grand Total ({trips.length} trips)</span>
                      <span className="text-2xl font-bold">₹{grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Tips */}
              <div className="mt-4 bg-amber-50 rounded-xl p-4 border border-amber-200">
                <p className="text-amber-800 font-medium text-sm mb-2">💡 Tips</p>
                <ul className="text-xs text-amber-700 space-y-1">
                  <li>• Use odometer readings for accurate distance</li>
                  <li>• Add multiple trips and submit all at once</li>
                  <li>• Mark as billable if customer should be charged</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MileageExpensePage;
